"use client";

import Accordion from "@/components/ui/accordion";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/tailwind";
import { usePdpContext } from "@/providers/PdpProvider";
import { useEffect, useMemo, useRef, useState } from "react";
import { ProductDetailsTabProps } from "../types";
import AboutProduct from "./variants/AboutProduct";
import GiftPackaging from "./variants/GiftPackaging";
import ProductInfo from "./variants/ProductInfo";
import BundleProductInfo from "./variants/BundleProductInfo";
import ShippingNReturns from "./variants/ShippingNReturns";
import SizeNFit from "./variants/SizeNFit/SizeNFit";
import CustomTabs from "./variants/CustomTabs";
import GuideTabVariant from "./variants/GuideTabVariant";
import CareTabVariant from "./variants/CareTabVariant";
import FaqTabVariant from "./variants/FaqTabVariant";

const ProductDetailsTab = ({
  data,
  lang,
  category,
  categoryURL,
  layoutDetails,
  altText,
  bundleVariantsData,
  bundleVariantsLoading,
}: ProductDetailsTabProps & {
  categoryURL: string;
  altText: string;
  bundleVariantsData?: Array<{
    id: string;
    index: number;
    title: string;
    image: string;
    productInfo: Array<{ label: string | undefined; value: string | undefined }>;
  }>;
  bundleVariantsLoading?: boolean;
}) => {
  // states
  const [isClient, setIsClient] = useState(false);

  // hooks
  const { pdpData, setPdpData } = usePdpContext();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const eleRef = useRef<null | HTMLDivElement>(null);

  // consts
  const allOptions = useMemo(() => {
    return data?.map((option) => {
      switch (option.type) {
        case "productDetails":
          return option?.data?.productInfo?.length > 0
            ? {
              id: option.key,
              type: "productDetails",
              title: option.label,
              content: option.data?.isBundle && bundleVariantsData ? (
                <BundleProductInfo
                  id={option.key}
                  data={option.data}
                  key={option.key}
                  label={option.label}
                  altText={altText}
                  bundleVariantsData={bundleVariantsData}
                  bundleVariantsLoading={bundleVariantsLoading}
                  lang={lang}
                />
              ) : (
                <ProductInfo
                  id={option.key}
                  data={option.data}
                  key={option.key}
                  label={option.label}
                  altText={altText}
                />
              ),
            }
            : undefined;

        case "about":
          return option?.data?.description ||
            option?.data?.feature1 ||
            option?.data?.feature2 ||
            option?.data?.feature3 ||
            option?.data?.feature4
            ? {
              id: option.key,
              type: "about",
              title: option.label,
              content: (
                <AboutProduct
                  id={option.key}
                  data={option.data}
                  label={option.label}
                  key={option.key}
                  altText={altText}
                />
              ),
            }
            : undefined;

        case "shippingAndReturn":
          return option?.data?.description
            ? {
              id: option.key,
              type: "shippingAndReturn",
              title: option.label,
              content: (
                <ShippingNReturns
                  key={option.key}
                  id={option.key}
                  data={option.data}
                  label={option.label}
                  altText={altText}
                />
              ),
            }
            : undefined;
        case "giftPackaging":
          return option?.data?.items && option?.data.items.length > 0
            ? {
              id: option.key,
              type: "giftPackaging",
              title: option.label,
              content: <GiftPackaging key={option.key} id={option.type} data={option.data} label={option.label} />,
            }
            : undefined;
        case "sizeAndFit":
          return option?.data?.measurementsTab?.productMeasurements?.items?.length > 0 &&
            option?.data?.sizeGuideTab?.leftSideContent?.description &&
            option?.data?.sizeGuideTab?.rightSideContent?.description
            ? {
              id: option.key,
              type: "sizeAndFit",
              title: option?.label,
              content: (
                <SizeNFit
                  id={option.key}
                  label={option.label}
                  data={option.data}
                  lang={lang}
                  category={category}
                  layoutDetails={layoutDetails}
                  key={"size-and-fit"}
                  categoryURL={categoryURL}
                  currentTab={pdpData?.sizeAndFitChildTab ?? 0}
                  handleUpdateContentHeight={handleUpdateContentHeight}
                />
              ),
            }
            : undefined;
        case "customTab":
          return option?.data?.description ? {
            id: option.key,
            type: "customTab",
            title: option?.label,
            content: (
              <CustomTabs data={option.data} key={option.key} label={option.label} id={option.type} />
            )
          } : undefined;
        case "guideTab":
          return option?.data?.sections?.length > 0 ? {
            id: option.key,
            type: "guideTab",
            title: option?.label,
            content: (
              <GuideTabVariant data={option.data} key={option.key} />
            )
          } : undefined;
        case "careTab":
          return option?.data?.subTabs?.length > 0 ? {
            id: option.key,
            type: "careTab",
            title: option?.label,
            content: (
              <CareTabVariant data={option.data} key={option.key} />
            )
          } : undefined;
        case "faqTab":
          return option?.data?.sections?.length > 0 ? {
            id: option.key,
            type: "faqTab",
            title: option?.label,
            content: (
              <FaqTabVariant data={option.data} key={option.key} />
            )
          } : undefined;
      }
    });
  }, [data, pdpData, bundleVariantsData, bundleVariantsLoading, altText, lang]);

  const options = useMemo(() => allOptions?.filter((v) => v !== undefined), [allOptions]);

  // States
  const [openIndices, setOpenIndices] = useState<number[]>();
  const [toggle, setToggle] = useState(false);
  const [key, setKey] = useState(0);

  let selected = options?.findIndex(
    (v) => v?.id === pdpData?.productDetailsTab || v?.type === pdpData?.productDetailsTab
  );
  selected = selected < 0 ? (openIndices?.[0] ?? 0) : selected; //if no tabs are selected, select the default tab

  function handleUpdateContentHeight() {
    setToggle((prev) => !prev);
  }

  const handleClick = (_index: number, tabId: string) => {
    setPdpData({ ...pdpData, productDetailsTab: tabId });
  };

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!pdpData.productDetailsTab) return;

    // Find the actual tab so we can use its concrete ID to find the element wrapper if needed,
    // though the scroll is primarily handled by the size guide button itself now.
    const idx = options.findIndex(
      (op) => op.id === pdpData.productDetailsTab || op.type === pdpData.productDetailsTab
    );

    if (idx === -1) return;

    const actualTabId = options[idx]?.id;
    const ele = document.getElementById(actualTabId);

    if (ele) {
      eleRef.current = ele as HTMLDivElement;
      // We removed the hardcoded window.scrollTo here as we rely on scrollToElementWithOffsets
    }

    setOpenIndices([idx]);
    setKey((prev) => prev + 1);
  }, [pdpData, options.length]);

  useEffect(() => {
    const idx = options?.findIndex((v) => v?.type === "productDetails");
    setOpenIndices(idx < 0 ? [0] : [idx]);
  }, [options.length]);


  if (!isClient) return <></>;

  return (
    <div id="wrapper">
      {!isMobile && options.length > 0 && (
        <section id="view" className="h-full bg-[#FAFAFA]">
          <div id="product-info-tabs" className="h-20 bg-[#BBB6A5]">
            <div className="container flex h-full w-full items-center justify-center">
              <div className="flex items-center justify-evenly gap-8 font-semibold">
                {options?.map((item, index) => (
                  <button
                    key={index}
                    className={cn(
                      "rounded-none px-4 py-2 text-sm text-white outline-1 outline-white focus:outline-1 focus:outline-white",
                      selected === index ? "bg-white text-black" : "bg-transparent",
                    )}
                    data-item-id={item.id}
                    onClick={() => handleClick(index, item.id)}
                  >
                    {item?.title}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex min-h-[32rem] items-center justify-center overflow-hidden px-12 xl:px-0">
            {options?.[selected]?.content}
          </div>
        </section>
      )}

      {/* for mobile view */}
      {isMobile ? (
        <Accordion
          key={"accordion-" + key}
          autoCollapse
          headerClasses="px-4"
          items={options ?? []}
          openIndices={openIndices}
          className="h-full bg-tertiary-5"
          reCalculateHeight={toggle}
        />
      ) : (
        <></>
      )}
    </div>
  );
};

export default ProductDetailsTab;